<script>
import ContactPanel from 'dashboard/routes/dashboard/conversation/ContactPanel.vue';
import TaskList from './TaskList.vue';
import ProductList from './ProductList.vue';
import ProductsManager from './ProductsManager.vue';
import DealInstallments from './DealInstallments.vue';
import DealDrawerContent from './DealDrawerContent.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  name: 'KanbanDrawer',
  components: {
    ContactPanel,
    TaskList,
    ProductList,
    ProductsManager,
    DealInstallments,
    DealDrawerContent,
    NextButton,
  },
  props: {
    conversation: { type: Object, required: true },
  },
  emits: ['close', 'deleted'],
  data() {
    return {
      localConversation: { ...this.conversation },
      activeDrawerTab: 'details',
      targetDealStatus: null,
      selectedLostReason: '',
      selectedPaymentMethod: '',
      installmentCount: 1,
      firstDueDate: new Date().toISOString().slice(0, 10),
      showCustomOriginInput: false,
      customOriginValue: '',
      showProductsManager: false,
      editingClosedAt: false,
    };
  },
  computed: {
    currentAccountId() {
      return this.$store.getters.getCurrentAccountId;
    },
    currentAccount() {
      return this.$store.getters['accounts/getAccount'](this.currentAccountId);
    },
    // Modo `deals`: a gaveta larga custom_attributes e passa a ler/gravar
    // o negócio pela API do CRM (ver DealDrawerContent.vue).
    isDealsMode() {
      return this.currentAccount?.crm_mode === 'deals';
    },
    dealId() {
      return this.localConversation?.deal_id;
    },
    drawerDealValue() {
      return this.localConversation?.custom_attributes?.deal_value || '';
    },
    drawerContactName() {
      return this.localConversation?.meta?.sender?.name || 'Contato';
    },
    drawerDealTitle() {
      return this.localConversation?.custom_attributes?.deal_title || '';
    },
    drawerLeadSource() {
      const ca = this.localConversation?.custom_attributes;
      if (!ca || !ca.lead_source_ad_name) return null;
      return {
        type: ca.lead_source_type,
        platform: ca.lead_source_platform,
        ad_name: ca.lead_source_ad_name,
        ad_body: ca.lead_source_ad_body,
        ad_id: ca.lead_source_ad_id,
        source_url: ca.lead_source_source_url,
        media_url: ca.lead_source_media_url,
        thumbnail_url: ca.lead_source_thumbnail_url,
        click_id: ca.lead_source_click_id,
        provider: ca.lead_source_provider,
      };
    },
    drawerDealStatus() {
      return this.localConversation?.custom_attributes?.deal_status || null;
    },
    drawerDealClosedAt() {
      return this.localConversation?.custom_attributes?.deal_closed_at || null;
    },
    // For the closed_at date input — needs YYYY-MM-DD, not ISO timestamp.
    drawerDealClosedAtDate() {
      const iso = this.drawerDealClosedAt;
      if (!iso) return '';
      const d = new Date(iso);
      return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
    },
    isCurrentUserAdmin() {
      return this.$store.getters.getCurrentRole === 'administrator';
    },
    drawerDealOrigin() {
      return this.localConversation?.custom_attributes?.deal_origin || '';
    },
    knownOrigins() {
      // Collect unique origins from all conversations in the kanban
      if (!this.kanbanCards) return [];
      const origins = new Set();
      Object.values(this.kanbanCards)
        .flat()
        .forEach(c => {
          const origin = c.custom_attributes?.deal_origin;
          if (origin) origins.add(origin);
        });
      return [...origins].sort();
    },
    drawerInstallments() {
      return this.localConversation?.custom_attributes?.deal_installments || [];
    },
    drawerDealPaidValue() {
      // Com parcelas, o valor pago é derivado (soma das pagas);
      // sem parcelas, vale o campo manual legado.
      if (this.drawerInstallments.length) {
        return this.drawerInstallments.reduce(
          (acc, p) => acc + (p.paid ? parseFloat(p.value) || 0 : 0),
          0
        );
      }
      return (
        parseFloat(
          this.localConversation?.custom_attributes?.deal_paid_value
        ) || 0
      );
    },
    drawerDealReceivable() {
      const total =
        parseFloat(this.localConversation?.custom_attributes?.deal_value) || 0;
      return Math.max(0, total - this.drawerDealPaidValue);
    },
    formattedDealReceivable() {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(this.drawerDealReceivable);
    },
    drawerTasks() {
      return this.localConversation?.custom_attributes?.crm_tasks || [];
    },
    wonLabel() {
      return this.currentAccount?.custom_attributes?.crm_won_label || 'ganho';
    },
    lostLabel() {
      return (
        this.currentAccount?.custom_attributes?.crm_lost_label || 'perdido'
      );
    },
    crmLostReasons() {
      const items = this.currentAccount?.custom_attributes?.crm_lost_reasons;
      return items && items.length > 0
        ? items
        : [
            'Preço',
            'Sem interesse',
            'Fechou com concorrente',
            'Parou de responder',
            'Outros',
          ];
    },
    crmPaymentMethods() {
      const items = this.currentAccount?.custom_attributes?.crm_payment_methods;
      return items && items.length > 0
        ? items
        : ['Pix', 'Cartão de Crédito', 'Boleto', 'Transferência Bancária'];
    },
    crmOrigins() {
      const items = this.currentAccount?.custom_attributes?.crm_origins;
      return items && items.length > 0
        ? items
        : ['WhatsApp', 'Instagram', 'Google Ads', 'Indicação', 'Website'];
    },
    drawerProducts() {
      return this.localConversation?.custom_attributes?.crm_products || [];
    },
    catalogProducts() {
      return this.currentAccount?.custom_attributes?.crm_products || [];
    },
    overdueTasks() {
      return this.drawerTasks.filter(
        t => !t.done && t.dueDate && new Date(t.dueDate) < new Date()
      );
    },
    drawerTabs() {
      return [
        { id: 'details', label: 'Detalhes', badge: 0 },
        { id: 'tasks', label: 'Tarefas', badge: this.overdueTasks.length || 0 },
        { id: 'products', label: 'Produtos', badge: 0 },
      ];
    },
  },
  watch: {
    conversation: {
      handler(newVal) {
        this.localConversation = { ...newVal };
        this.activeDrawerTab = 'details';
        this.targetDealStatus = null;
        this.selectedLostReason = '';
        this.selectedPaymentMethod = '';
        this.installmentCount = 1;
        this.firstDueDate = new Date().toISOString().slice(0, 10);
        this.showCustomOriginInput = false;
        this.customOriginValue = '';
        // Modo `deals`: DealDrawerContent cuida do próprio carregamento
        // (watch em dealId); a conversa-fantasia do dealToCard não tem
        // inbox real nem custom_attributes pra inicializar aqui.
        if (this.isDealsMode) return;
        this.initDrawer();
      },
    },
  },
  mounted() {
    if (this.isDealsMode) return;
    this.initDrawer();
  },
  methods: {
    initDrawer() {
      const conversation = this.localConversation;
      if (!conversation || !conversation.id) return;
      if (conversation.inbox_id) {
        this.$store.dispatch('inboxAssignableAgents/fetch', [
          conversation.inbox_id,
        ]);
      }
      if (
        !conversation.custom_attributes?.deal_origin &&
        conversation.inbox_id
      ) {
        const inbox = this.$store.getters['inboxes/getInbox'](
          conversation.inbox_id
        );
        if (inbox?.name) {
          this.$store.dispatch('updateCustomAttributes', {
            conversationId: conversation.id,
            customAttributes: { deal_origin: inbox.name },
          });
          this.localConversation = {
            ...conversation,
            custom_attributes: {
              ...conversation.custom_attributes,
              deal_origin: inbox.name,
            },
          };
        }
      }
      try {
        this.$store.dispatch('setActiveChat', { data: conversation });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[KanbanDrawer] setActiveChat falhou:', e);
      }
    },
    updateDealValue(event) {
      const val = event.target.value;
      if (!this.localConversation) return;
      this.$store.dispatch('updateCustomAttributes', {
        conversationId: this.localConversation.id,
        customAttributes: { deal_value: val },
      });
    },
    updateDealTitle(event) {
      if (!this.localConversation) return;
      const title = event.target.value.trim();
      this.$store.dispatch('updateCustomAttributes', {
        conversationId: this.localConversation.id,
        customAttributes: { deal_title: title },
      });
      this.localConversation = {
        ...this.localConversation,
        custom_attributes: {
          ...this.localConversation.custom_attributes,
          deal_title: title,
        },
      };
    },
    updateDealClosedAt(event) {
      // Admin-only: lets the team correct the recorded close date
      // when a deal was marked on the wrong day.
      if (!this.localConversation || !this.isCurrentUserAdmin) return;
      const dateStr = event.target.value;
      if (!dateStr) return;

      // Keep the existing time-of-day if there was one, only swap the date.
      const existing = this.drawerDealClosedAt
        ? new Date(this.drawerDealClosedAt)
        : new Date();
      const [y, m, d] = dateStr.split('-').map(Number);
      const next = new Date(existing);
      next.setFullYear(y, m - 1, d);

      const iso = next.toISOString();
      this.$store.dispatch('updateCustomAttributes', {
        conversationId: this.localConversation.id,
        customAttributes: { deal_closed_at: iso },
      });
      this.localConversation = {
        ...this.localConversation,
        custom_attributes: {
          ...this.localConversation.custom_attributes,
          deal_closed_at: iso,
        },
      };
      this.editingClosedAt = false;
    },
    updateDealOrigin(val) {
      if (!this.localConversation) return;
      this.$store.dispatch('updateCustomAttributes', {
        conversationId: this.localConversation.id,
        customAttributes: { deal_origin: val },
      });
      this.localConversation = {
        ...this.localConversation,
        custom_attributes: {
          ...this.localConversation.custom_attributes,
          deal_origin: val,
        },
      };
    },
    handleOriginSelect(event) {
      const val = event.target.value;
      if (val === '__custom__') {
        this.showCustomOriginInput = true;
        this.customOriginValue = '';
        return;
      }
      this.updateDealOrigin(val);
    },
    saveCustomOrigin() {
      if (this.customOriginValue.trim()) {
        this.updateDealOrigin(this.customOriginValue.trim());
      }
      this.showCustomOriginInput = false;
      this.customOriginValue = '';
    },
    updateDealPaidValue(event) {
      if (this.drawerInstallments.length) return; // derivado das parcelas
      const val = event.target.value;
      if (!this.localConversation) return;
      this.$store.dispatch('updateCustomAttributes', {
        conversationId: this.localConversation.id,
        customAttributes: { deal_paid_value: val },
      });
      // Update local copy for immediate UI feedback
      this.localConversation = {
        ...this.localConversation,
        custom_attributes: {
          ...this.localConversation.custom_attributes,
          deal_paid_value: val,
        },
      };
    },
    updateInstallments(installments) {
      if (!this.localConversation) return;
      const paidSum = installments.reduce(
        (acc, p) => acc + (p.paid ? parseFloat(p.value) || 0 : 0),
        0
      );
      const customAttributes = {
        deal_installments: installments,
        deal_paid_value: String(paidSum),
      };
      this.$store.dispatch('updateCustomAttributes', {
        conversationId: this.localConversation.id,
        customAttributes,
      });
      this.localConversation = {
        ...this.localConversation,
        custom_attributes: {
          ...this.localConversation.custom_attributes,
          ...customAttributes,
        },
      };
    },
    updateDrawerTasks(tasks) {
      if (!this.localConversation) return;
      this.$store.dispatch('updateCustomAttributes', {
        conversationId: this.localConversation.id,
        customAttributes: { crm_tasks: tasks },
      });
      // Update local copy so UI reflects immediately
      this.localConversation = {
        ...this.localConversation,
        custom_attributes: {
          ...this.localConversation.custom_attributes,
          crm_tasks: tasks,
        },
      };
    },
    updateDrawerProducts(products) {
      if (!this.localConversation) return;
      // Calculate deal_value from products
      const total = products.reduce((acc, item) => {
        const catalogItem = this.catalogProducts.find(
          p => p.id === item.productId
        );
        if (!catalogItem) return acc;
        const price =
          catalogItem.price *
          (item.quantity || 1) *
          (1 - (item.discount || 0) / 100);
        return acc + price;
      }, 0);

      this.$store.dispatch('updateCustomAttributes', {
        conversationId: this.localConversation.id,
        customAttributes: {
          crm_products: products,
          deal_value: total.toFixed(2),
        },
      });
      this.localConversation = {
        ...this.localConversation,
        custom_attributes: {
          ...this.localConversation.custom_attributes,
          crm_products: products,
          deal_value: total.toFixed(2),
        },
      };
    },
    saveCatalogProducts(products) {
      this.$store.dispatch('accounts/update', {
        custom_attributes: { crm_products: products },
      });
    },
    setDealStatus(status) {
      if (!this.localConversation) return;

      // Unset workflow
      if (this.drawerDealStatus === status) {
        this.confirmDealStatusChange(null);
        this.targetDealStatus = null;
        return;
      }

      // Open confirm UI
      this.targetDealStatus = status;
      this.selectedLostReason =
        this.localConversation?.custom_attributes?.deal_lost_reason || '';
      this.selectedPaymentMethod =
        this.localConversation?.custom_attributes?.deal_payment_method || '';
    },
    confirmDealStatusChange(status) {
      if (!this.localConversation) return;

      const existingClosedAt =
        this.localConversation?.custom_attributes?.deal_closed_at;
      // deal_closed_at is the historical timestamp of when the deal was
      // first closed. Once set, we preserve it across re-marks/un-marks
      // so the "month of close" stays stable for reporting. Without this
      // guard, clicking the won/lost button a second time wiped the date
      // and then re-stamped it with `now`, silently moving the sale to
      // the current month.
      // Admins can correct the date manually via the date input below.
      let closedAt;
      if (!status) {
        // Unmarking — keep the historical timestamp as audit trail.
        closedAt = existingClosedAt;
      } else {
        closedAt = existingClosedAt || new Date().toISOString();
      }

      const customAttributes = {
        deal_status: status,
        deal_closed_at: closedAt,
      };

      if (status === 'lost') {
        customAttributes.deal_lost_reason = this.selectedLostReason;
      } else if (status === 'won') {
        customAttributes.deal_payment_method = this.selectedPaymentMethod;
        const total =
          parseFloat(this.localConversation?.custom_attributes?.deal_value) ||
          0;
        const count = Math.max(1, parseInt(this.installmentCount, 10) || 1);
        const installments = this.buildInstallments(
          total,
          count,
          this.firstDueDate
        );
        if (count === 1) {
          // À vista mantém o comportamento anterior: ganho = pago integral
          installments[0].paid = true;
          customAttributes.deal_paid_value = String(total);
        } else {
          customAttributes.deal_paid_value = '0';
        }
        customAttributes.deal_installments = installments;
      }

      this.$store.dispatch('updateCustomAttributes', {
        conversationId: this.localConversation.id,
        customAttributes: customAttributes,
      });

      this.localConversation = {
        ...this.localConversation,
        custom_attributes: {
          ...this.localConversation.custom_attributes,
          ...customAttributes,
        },
      };

      this.applyDealOutcomeSideEffects(status);

      this.targetDealStatus = null;
    },
    async applyDealOutcomeSideEffects(status) {
      if (!this.localConversation) return;
      const dealLabels = [this.wonLabel, this.lostLabel];
      const targetLabel =
        status === 'won'
          ? this.wonLabel
          : status === 'lost'
            ? this.lostLabel
            : null;
      try {
        // Etiqueta na conversa (parte das atuais, remove resultado oposto, append do novo)
        const convLabels = [...(this.localConversation.labels || [])].filter(
          l => !dealLabels.includes(l)
        );
        if (targetLabel) convLabels.push(targetLabel);
        await this.$store.dispatch('conversationLabels/update', {
          conversationId: this.localConversation.id,
          labels: convLabels,
        });
        this.localConversation = {
          ...this.localConversation,
          labels: convLabels,
        };

        // Etiqueta no contato (lê atuais, preserva as demais)
        const contactId = this.localConversation?.meta?.sender?.id;
        if (contactId) {
          await this.$store.dispatch('contactLabels/get', contactId);
          const current =
            this.$store.getters['contactLabels/getContactLabels'](contactId) ||
            [];
          const contactLabels = [...current].filter(
            l => !dealLabels.includes(l)
          );
          if (targetLabel) contactLabels.push(targetLabel);
          await this.$store.dispatch('contactLabels/update', {
            contactId,
            labels: contactLabels,
          });
        }

        // Adiar a conversa até a próxima resposta (só ao marcar; desmarcar não mexe).
        // snoozedUntil null = "até a próxima resposta": some da fila e reabre a MESMA
        // conversa quando o lead responder (sem duplicar / sem recomeçar do zero).
        if (status) {
          await this.$store.dispatch('toggleStatus', {
            conversationId: this.localConversation.id,
            status: 'snoozed',
            snoozedUntil: null,
          });
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[KanbanDrawer] aplicar resultado do negócio falhou:', e);
      }
    },
    buildInstallments(total, count, firstDueDateStr) {
      const fallback = new Date().toISOString().slice(0, 10);
      const [y, m, d] = (firstDueDateStr || fallback).split('-').map(Number);
      const base = Math.floor((total / count) * 100) / 100;
      const toDateStr = dt =>
        `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
      return Array.from({ length: count }, (_, i) => {
        const due = new Date(y, m - 1 + i, d);
        // Dia 31 em mês curto → recua pro último dia do mês pretendido
        if (due.getDate() !== d) due.setDate(0);
        const isLast = i === count - 1;
        const value = isLast
          ? Math.round((total - base * (count - 1)) * 100) / 100
          : base;
        return { n: i + 1, value, dueDate: toDateStr(due), paid: false };
      });
    },
    cancelDealStatusUpdate() {
      this.targetDealStatus = null;
    },
    formatClosedAt(isoStr) {
      if (!isoStr) return '';
      return new Date(isoStr).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
};
</script>

<template>
  <div>
    <!-- Conversation Drawer -->
    <div
      class="fixed inset-0 z-50 flex justify-end"
      @click.self="$emit('close')"
    >
      <div class="absolute inset-0 bg-black/40" @click="$emit('close')" />
      <!-- Modo `deals` usa gaveta mais larga: a aba Detalhes ganhou contato,
           campos personalizados e o bloco de requisitos, e 420px empilhava
           tudo numa coluna só. O conversacional fica como estava. -->
      <div
        class="relative max-w-full h-full bg-n-background shadow-2xl flex flex-col animate-slide-in"
        :class="isDealsMode ? 'w-[640px]' : 'w-[420px]'"
      >
        <!-- Modo `deals`: componente à parte, lê/grava pela API do CRM -->
        <DealDrawerContent
          v-if="isDealsMode"
          :deal-id="dealId"
          @close="$emit('close')"
          @deleted="$emit('deleted', $event)"
        />

        <!-- Drawer Header -->
        <div
          v-if="!isDealsMode"
          class="px-6 py-4 border-b border-n-weak flex justify-between items-center"
        >
          <h2 class="text-lg font-semibold m-0 text-n-slate-12">
            Detalhes da Conversa
          </h2>
          <button
            class="p-1 rounded hover:bg-n-alpha-2 border-none bg-transparent cursor-pointer"
            @click="$emit('close')"
          >
            <span class="i-lucide-x w-5 h-5 text-n-slate-10" />
          </button>
        </div>

        <!-- Tab Bar -->
        <div v-if="!isDealsMode" class="flex border-b border-n-weak">
          <button
            v-for="tab in drawerTabs"
            :key="tab.id"
            class="flex-1 px-4 py-2.5 text-sm font-medium border-none cursor-pointer transition-colors"
            :class="
              activeDrawerTab === tab.id
                ? 'text-n-brand border-b-2 border-n-brand bg-transparent'
                : 'text-n-slate-10 hover:text-n-slate-12 bg-transparent'
            "
            @click="activeDrawerTab = tab.id"
          >
            {{ tab.label }}
            <span
              v-if="tab.badge"
              class="ml-1 text-xs bg-n-ruby-9 text-white rounded-full px-1.5 py-0.5"
              >{{ tab.badge }}</span
            >
          </button>
        </div>

        <!-- Tab Content -->
        <div v-if="!isDealsMode" class="flex-1 overflow-y-auto">
          <!-- Detalhes Tab -->
          <div
            v-if="activeDrawerTab === 'details'"
            class="flex flex-col h-full overflow-hidden"
          >
            <!-- Deal Value (Kanban Specific) -->
            <div class="px-4 py-4 border-b border-n-weak bg-n-alpha-1">
              <!-- Nome do Card -->
              <div class="mb-3">
                <label
                  class="block text-xs font-semibold text-n-slate-11 mb-1 uppercase tracking-wide"
                  >Nome do Card</label
                >
                <input
                  :value="drawerDealTitle"
                  type="text"
                  :placeholder="drawerContactName"
                  class="w-full px-2 py-1.5 rounded-md text-sm bg-n-surface-1 border border-n-weak text-n-slate-12 focus:ring-2 focus:ring-n-brand font-semibold"
                  @change="updateDealTitle($event)"
                />
                <p class="text-xs text-n-slate-10 mt-1 m-0">
                  Deixe em branco para usar o nome do contato.
                </p>
              </div>

              <div class="flex items-center gap-3 mb-3">
                <div class="flex-1">
                  <label
                    class="block text-xs font-semibold text-n-slate-11 mb-1 uppercase tracking-wide"
                    >Valor do Negócio</label
                  >
                  <div class="flex items-center gap-1">
                    <span class="text-xs font-bold text-n-blue-11">R$</span>
                    <input
                      :value="drawerDealValue"
                      type="number"
                      step="0.01"
                      placeholder="0,00"
                      class="w-full px-2 py-1.5 rounded-md text-sm bg-n-surface-1 border border-n-weak text-n-slate-12 focus:ring-2 focus:ring-n-brand font-semibold"
                      @change="updateDealValue($event)"
                    />
                  </div>
                </div>
                <div class="flex-1">
                  <label
                    class="block text-xs font-semibold text-n-slate-11 mb-1 uppercase tracking-wide"
                    >Origem</label
                  >
                  <select
                    v-if="!showCustomOriginInput"
                    :value="drawerDealOrigin"
                    class="w-full px-2 py-1.5 rounded-md text-sm bg-n-surface-1 border border-n-weak text-n-slate-12 focus:ring-2 focus:ring-n-brand cursor-pointer"
                    @change="handleOriginSelect($event)"
                  >
                    <option value="" disabled>Selecionar...</option>
                    <option v-for="o in crmOrigins" :key="o" :value="o">
                      {{ o }}
                    </option>
                    <option value="__custom__">✏️ Outro...</option>
                  </select>
                  <div v-else class="flex items-center gap-1">
                    <input
                      v-model="customOriginValue"
                      type="text"
                      placeholder="Digite a origem..."
                      class="flex-1 px-2 py-1.5 rounded-md text-sm bg-n-surface-1 border border-n-weak text-n-slate-12 focus:ring-2 focus:ring-n-brand"
                      @keyup.enter="saveCustomOrigin"
                    />
                    <button
                      class="px-2 py-1 text-xs rounded bg-woot-500 text-white border-0 cursor-pointer hover:bg-woot-600"
                      @click="saveCustomOrigin"
                    >
                      OK
                    </button>
                    <button
                      class="px-2 py-1 text-xs rounded bg-n-alpha-2 text-n-slate-11 border border-n-weak cursor-pointer"
                      @click="showCustomOriginInput = false"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              </div>

              <!-- Deal Status Buttons -->
              <div v-if="!targetDealStatus" class="mt-3 flex gap-2">
                <button
                  class="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer border-0"
                  :class="
                    drawerDealStatus === 'won'
                      ? 'bg-woot-500 text-white shadow-md'
                      : 'bg-n-blue-3 text-n-blue-11 outline outline-1 outline-n-blue-6 hover:bg-n-blue-4'
                  "
                  @click="setDealStatus('won')"
                >
                  🏆 Ganho
                </button>
                <button
                  class="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer border-0"
                  :class="
                    drawerDealStatus === 'lost'
                      ? 'bg-n-ruby-9 text-white shadow-md'
                      : 'bg-n-ruby-3 text-n-ruby-11 outline outline-1 outline-n-ruby-6 hover:bg-n-ruby-4'
                  "
                  @click="setDealStatus('lost')"
                >
                  ❌ Perdido
                </button>
              </div>

              <!-- Deal Status Confirmation Form -->
              <div
                v-else
                class="mt-3 p-3 bg-white dark:bg-slate-800 border border-n-weak rounded-lg shadow-sm"
              >
                <label class="block text-xs font-semibold text-n-slate-11 mb-2">
                  {{
                    targetDealStatus === 'won'
                      ? 'Forma de Pagamento'
                      : 'Motivo da Perda'
                  }}
                </label>

                <select
                  v-if="targetDealStatus === 'won'"
                  v-model="selectedPaymentMethod"
                  class="w-full pl-3 pr-6 py-2 border border-n-weak rounded-md text-sm bg-n-surface-1 mb-3"
                >
                  <option value="" disabled>
                    Selecione a forma de pagamento
                  </option>
                  <option
                    v-for="method in crmPaymentMethods"
                    :key="method"
                    :value="method"
                  >
                    {{ method }}
                  </option>
                </select>

                <div
                  v-if="targetDealStatus === 'won'"
                  class="flex items-center gap-2 mb-3"
                >
                  <div class="flex-1">
                    <label
                      class="block text-xs font-semibold text-n-slate-11 mb-1"
                      >Nº de parcelas</label
                    >
                    <input
                      v-model.number="installmentCount"
                      type="number"
                      min="1"
                      max="60"
                      class="w-full px-3 py-2 border border-n-weak rounded-md text-sm bg-n-surface-1"
                    />
                  </div>
                  <div class="flex-1">
                    <label
                      class="block text-xs font-semibold text-n-slate-11 mb-1"
                      >1ª parcela</label
                    >
                    <input
                      v-model="firstDueDate"
                      type="date"
                      class="w-full px-3 py-2 border border-n-weak rounded-md text-sm bg-n-surface-1"
                    />
                  </div>
                </div>

                <select
                  v-if="targetDealStatus === 'lost'"
                  v-model="selectedLostReason"
                  class="w-full pl-3 pr-6 py-2 border border-n-weak rounded-md text-sm bg-n-surface-1 mb-3"
                >
                  <option value="" disabled>Selecione o motivo da perda</option>
                  <option
                    v-for="reason in crmLostReasons"
                    :key="reason"
                    :value="reason"
                  >
                    {{ reason }}
                  </option>
                </select>

                <div class="flex items-center gap-2">
                  <NextButton
                    label="Cancelar"
                    size="sm"
                    variant="faded"
                    color="slate"
                    class="flex-1"
                    @click="cancelDealStatusUpdate"
                  />
                  <NextButton
                    label="Confirmar"
                    size="sm"
                    :color="targetDealStatus === 'won' ? 'teal' : 'ruby'"
                    class="flex-1"
                    :disabled="
                      targetDealStatus === 'won'
                        ? !selectedPaymentMethod
                        : !selectedLostReason
                    "
                    @click="confirmDealStatusChange(targetDealStatus)"
                  />
                </div>
              </div>
              <p
                v-if="drawerDealClosedAt"
                class="mt-2 text-xs text-n-slate-11 m-0"
              >
                <span v-if="!editingClosedAt">
                  {{ drawerDealStatus === 'won' ? 'Ganho' : 'Perdido' }} em
                  {{ formatClosedAt(drawerDealClosedAt) }}
                  <button
                    v-if="isCurrentUserAdmin"
                    type="button"
                    class="ml-2 text-n-brand hover:underline"
                    @click="editingClosedAt = true"
                  >
                    editar
                  </button>
                </span>
                <span v-else class="inline-flex items-center gap-2">
                  Data:
                  <input
                    type="date"
                    :value="drawerDealClosedAtDate"
                    class="px-2 py-1 rounded-md bg-n-background border border-n-weak text-n-slate-12"
                    @change="updateDealClosedAt"
                  />
                  <button
                    type="button"
                    class="text-n-slate-11 hover:underline"
                    @click="editingClosedAt = false"
                  >
                    cancelar
                  </button>
                </span>
                <br />
                <span
                  v-if="
                    drawerDealStatus === 'won' &&
                    localConversation?.custom_attributes?.deal_payment_method
                  "
                  class="font-medium"
                >
                  Pagamento:
                  {{ localConversation.custom_attributes.deal_payment_method }}
                </span>
                <span
                  v-if="
                    drawerDealStatus === 'lost' &&
                    localConversation?.custom_attributes?.deal_lost_reason
                  "
                  class="font-medium text-n-ruby-11"
                >
                  Motivo:
                  {{ localConversation.custom_attributes.deal_lost_reason }}
                </span>
              </p>

              <!-- Valor Pago / A Receber (only for won deals) -->
              <div
                v-if="drawerDealStatus === 'won'"
                class="mt-3 p-3 bg-n-surface-1 border border-n-weak rounded-lg"
              >
                <div class="flex items-center gap-3 mb-2">
                  <div class="flex-1">
                    <label
                      class="block text-xs font-semibold text-n-slate-11 mb-1"
                      >Valor Pago</label
                    >
                    <div class="flex items-center gap-1">
                      <span class="text-xs font-bold text-n-slate-11">R$</span>
                      <input
                        :value="drawerDealPaidValue"
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="0,00"
                        :readonly="drawerInstallments.length > 0"
                        class="w-full px-2 py-1.5 rounded-md text-sm bg-n-background border border-n-weak text-n-slate-12 focus:ring-2 focus:ring-n-brand font-semibold"
                        :class="{
                          'opacity-60 cursor-not-allowed':
                            drawerInstallments.length > 0,
                        }"
                        @change="updateDealPaidValue($event)"
                      />
                    </div>
                  </div>
                  <div class="flex-1">
                    <label
                      class="block text-xs font-semibold text-n-slate-11 mb-1"
                      >A Receber</label
                    >
                    <div
                      class="px-2 py-1.5 rounded-md text-sm font-bold"
                      :class="
                        drawerDealReceivable > 0
                          ? 'text-amber-500 bg-amber-500/10'
                          : 'text-emerald-500 bg-emerald-500/10'
                      "
                    >
                      {{ formattedDealReceivable }}
                    </div>
                  </div>
                </div>
                <div
                  v-if="drawerDealReceivable <= 0 && drawerDealPaidValue > 0"
                  class="text-xs text-emerald-500 font-medium flex items-center gap-1"
                >
                  ✅ Pagamento completo
                </div>
                <DealInstallments
                  v-if="drawerInstallments.length"
                  :installments="drawerInstallments"
                  :deal-value="
                    parseFloat(
                      localConversation?.custom_attributes?.deal_value
                    ) || 0
                  "
                  @update="updateInstallments"
                />
              </div>
            </div>

            <!-- Lead Source (Ad Referral) Card -->
            <div
              v-if="drawerLeadSource"
              class="px-4 py-3 border-b border-n-weak"
            >
              <div
                class="p-3 rounded-lg border border-violet-200 dark:border-violet-800 bg-violet-50 dark:bg-violet-900/20"
              >
                <div class="flex items-center gap-2 mb-2">
                  <span class="text-sm">📣</span>
                  <span
                    class="text-xs font-semibold text-violet-700 dark:text-violet-300 uppercase tracking-wide"
                    >Origem do Lead</span
                  >
                  <span
                    v-if="drawerLeadSource.platform"
                    class="ml-auto inline-flex items-center text-xs font-medium bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300 px-2 py-0.5 rounded-full"
                  >
                    {{ drawerLeadSource.platform }}
                  </span>
                </div>
                <p
                  v-if="drawerLeadSource.ad_name"
                  class="text-sm font-semibold text-n-slate-12 m-0 mb-1"
                >
                  {{ drawerLeadSource.ad_name }}
                </p>
                <p
                  v-if="drawerLeadSource.ad_body"
                  class="text-xs text-n-slate-11 m-0 mb-2 line-clamp-2"
                  style="
                    display: -webkit-box;
                    -webkit-line-clamp: 2;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                  "
                >
                  {{ drawerLeadSource.ad_body }}
                </p>
                <div class="flex items-center gap-2">
                  <img
                    v-if="
                      drawerLeadSource.thumbnail_url ||
                      drawerLeadSource.media_url
                    "
                    :src="
                      drawerLeadSource.thumbnail_url ||
                      drawerLeadSource.media_url
                    "
                    class="w-10 h-10 rounded object-cover border border-n-weak"
                    @error="$event.target.style.display = 'none'"
                  />
                  <div class="flex-1 min-w-0">
                    <a
                      v-if="drawerLeadSource.source_url"
                      :href="drawerLeadSource.source_url"
                      target="_blank"
                      class="text-xs text-violet-600 dark:text-violet-400 hover:underline truncate block"
                    >
                      🔗 Ver anúncio original
                    </a>
                    <span
                      v-if="drawerLeadSource.ad_id"
                      class="text-xs text-n-slate-10 block truncate"
                    >
                      ID: {{ drawerLeadSource.ad_id }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Standard Contact Panel Container -->
            <div class="flex-1 overflow-y-auto w-full kanban-contact-panel">
              <ContactPanel
                v-if="localConversation.id"
                :conversation-id="localConversation.id"
                :inbox-id="localConversation.inbox_id"
              />
            </div>
          </div>

          <!-- Tarefas Tab -->
          <TaskList
            v-if="activeDrawerTab === 'tasks'"
            :tasks="drawerTasks"
            @update-tasks="updateDrawerTasks"
          />

          <!-- Produtos Tab -->
          <ProductList
            v-if="activeDrawerTab === 'products'"
            :conversation-products="drawerProducts"
            :catalog-products="catalogProducts"
            @update-products="updateDrawerProducts"
          />
        </div>
      </div>
    </div>

    <!-- Products Manager Modal -->
    <ProductsManager
      v-if="!isDealsMode"
      :show="showProductsManager"
      :products="catalogProducts"
      @close="showProductsManager = false"
      @update-products="saveCatalogProducts"
    />
  </div>
</template>

<style scoped>
.animate-slide-in {
  animation: slideIn 0.25s ease-out;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
}

/* Hide the ContactPanel's internal close header since we have our own nested in Kanban */
.kanban-contact-panel :deep(.sidebar-actions-header) {
  display: none !important;
}
</style>
